/* MoistureGuard and OdorGuard come in packs: one product, variants 3, 6, 9 and
   12 Bags. In the cart (drawer and /cart) their stepper moves the line between
   those variants instead of changing its quantity — + on "3 Bags" is "6 Bags",
   not two lines' worth of "3 Bags".

   snippets/cart-pack-variant.liquid marks those rows with what a swap needs:
   the pack the line is on (its variant's position in product.variants), every
   variant's id with its own selling plans, and the line's plan and properties.
   The stepper on those rows shows the packs on the line — the variant's
   position times the line's quantity, so 3 Bags at quantity 2 reads 2, as
   its "2 x 3-Pack (6 bags total)" line does. It carries no name (so the cart form
   never submits it as a quantity) and tops out at 10 of the last pack (or at
   the line's own packs, if more), where QuantityInput.validateQtyRules
   greys + out.

   QuantityInput steps the number as it does for any line; the change it
   dispatches is caught here, on the way down, before CartItems sees it:
   - 0 (− at pack 1, or a typed 0) goes on to CartItems and removes the line,
     as − at quantity 1 does on every other line.
   - Anything else moves the guard's packs by exactly what the row moved:
     − on a row showing 8 is 7 packs, + on a row showing 3 is 4. The guard's
     packs (every paid line of it in the cart) are laid out again the way
     cart-guard-combine.js lays them out: up to the last pack, one line of
     that pack; above it, the last pack at the quantity that fits plus the
     rest as its own pack (7 packs is 12 Bags + 9 Bags). If any of those
     lines is on Autoship, every new line is (Autoship wins).

   A swap is two POSTs: /cart/update.js takes the guard's lines out,
   /cart/add.js puts the new ones in with the selling plan (mapped to each new
   variant's own plan) and properties, and answers with the sections to paint.
   Shopify has no single request that changes a line's variant. If the add
   fails, the old lines go back in as they were.

   window.AirlockCartLock serialises this with cart-gift-sync.js: a gift
   reconcile that read the cart between the two POSTs would see a subtotal
   short one line and could take the gift out. */
(() => {
  // Loaded by the drawer snippet and by the cart page section; one is enough.
  if (window.cartPackVariantQty) return;
  window.cartPackVariantQty = true;

  /* One cart mutation at a time, shared with cart.js and cart-gift-sync.js,
     which define it identically (whichever loads first wins). run() queues
     the task behind whatever holds the lock; held says whether something is
     running now, busy whether anything is running or queued. */
  if (!window.AirlockCartLock) {
    window.AirlockCartLock = (() => {
      let tail = Promise.resolve();
      let pending = 0;
      const release = () => {
        pending -= 1;
      };
      const lock = {
        held: false,
        get busy() {
          return pending > 0;
        },
        run(fn) {
          pending += 1;
          const result = tail.then(async () => {
            lock.held = true;
            try {
              return await fn();
            } finally {
              lock.held = false;
            }
          });
          tail = result.then(release, release);
          return result;
        },
      };
      return lock;
    })();
  }

  let swapping = false;

  const readJSON = (value, fallback) => {
    try {
      return JSON.parse(value) ?? fallback;
    } catch (e) {
      return fallback;
    }
  };

  const post = (url, body, tracked) =>
    (tracked ? window.fetch.bind(window) : window.untrackedCartFetch || window.fetch.bind(window))(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });

  /* The target variant's plan for the line's plan: same group, same place in
     it; failing that, the same name. null for a one-time line; undefined when
     the target has nothing that matches. */
  const mapPlan = (variants, from, to, planId, planName) => {
    if (!planId) return null;
    const source = variants[from - 1].plans || [];
    const target = variants[to - 1].plans || [];
    const at = source.findIndex((plan) => String(plan.p) === String(planId));
    if (at !== -1) {
      const group = source[at].g;
      const place = source.slice(0, at).filter((plan) => plan.g === group).length;
      const match = target.filter((plan) => plan.g === group)[place];
      if (match) return match.p;
    }
    const name = at !== -1 ? source[at].n : planName;
    const byName = target.find((plan) => plan.n === name);
    return byName ? byName.p : undefined;
  };

  const setControls = (row, disabled) => {
    row.querySelectorAll(".quantity__button, .quantity__input").forEach((control) => {
      control.disabled = disabled;
    });
  };

  const showError = (row) => {
    const message = (window.cartStrings && window.cartStrings.error) || "";
    // The row's own error slot: on /cart the page and the drawer both have a
    // line N, and a lookup by id would put a drawer error on the page's row.
    const text = row.querySelector(".cart-item__error-text");
    if (text) text.textContent = message;
    else {
      const errors = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
      if (errors) errors.textContent = message;
    }
  };

  /* The cart page's own sections are not the drawer, and the drawer is where
     <cart-gift-sync> lives. Paint it too, so the gift reconcile runs against
     the subtotal the swap left. */
  const pageDrawer = (cartItems) =>
    cartItems.tagName === "CART-DRAWER-ITEMS" ? null : document.querySelector("#CartDrawer .drawer__inner");

  const paintDrawer = (target, html) => {
    if (!target || !html) return;
    const doc =
      typeof window.cartSectionDoc === "function"
        ? window.cartSectionDoc(html)
        : new DOMParser().parseFromString(html, "text/html");
    const source = doc.querySelector(".drawer__inner");
    if (!source) return;
    if (window.transformText && html.indexOf("AIRLOCK") !== -1) window.transformText(source);
    if (typeof setInnerHTMLIfChanged === "function") setInnerHTMLIfChanged(target, source.innerHTML);
    else if (target.innerHTML !== source.innerHTML) target.innerHTML = source.innerHTML;
  };

  // Paint as a quantity change does: gifts settled first in the drawer, then
  // the same sections through the same writer.
  const paint = async (cartItems, sectionsToRender, sections, itemCount) => {
    const settled = await cartItems.settleGifts(sections, itemCount, { underLock: true });
    cartItems.renderSections(sectionsToRender, settled.sections, settled.itemCount);
    paintDrawer(pageDrawer(cartItems), sections["cart-drawer"]);
    if (typeof window.refreshAffirmMessaging === "function") window.refreshAffirmMessaging();
  };

  const isGift = (item) => !!(item.properties && item.properties._gift);
  const planOf = (item) => (item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null);
  const cleanProperties = (raw) => (raw && !Array.isArray(raw) && typeof raw === "object" ? raw : {});

  /* packs as lines, the way cart-guard-combine.js lays them out: one line up
     to the last pack, else the last pack at the quantity that fits plus the
     rest as its own pack. [{ to (1-based pack), quantity }] */
  const layout = (packs, max) => {
    if (packs <= max) return [{ to: packs, quantity: 1 }];
    const lines = [{ to: max, quantity: Math.floor(packs / max) }];
    if (packs % max) lines.push({ to: packs % max, quantity: 1 });
    return lines;
  };

  const shape = (list) =>
    list
      .map((e) => [e.id ?? e.variant_id, e.quantity, e.selling_plan ?? planOf(e) ?? null].join(":"))
      .sort()
      .join(",");

  const swap = async (cartItems, row, delta) => {
    const variants = readJSON(row.dataset.packVariants, []);
    if (!variants.length) throw new Error("Unknown pack");
    const packOf = (item) => variants.findIndex((v) => v.id === item.variant_id) + 1;

    // The cart as it is now, under the lock: the rows on screen may be a step behind.
    const current = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } });
    if (!current.ok) throw new Error("Cart read failed");
    const items = (await current.json()).items || [];
    const self = items.find((item) => item.key === row.dataset.lineKey);
    if (!self || !packOf(self)) throw new Error("Line not in cart");
    /* Every paid line of this guard moves together, so the packs come out
       right however they were split. Lines told apart by their properties
       are left out: one set would be lost (the combine leaves them too). */
    const props = (item) => JSON.stringify(cleanProperties(item.properties));
    const lines = items.filter(
      (item) =>
        item.product_id === self.product_id &&
        !isGift(item) &&
        item.final_price > 0 &&
        packOf(item) &&
        props(item) === props(self)
    );
    const packs = lines.reduce((sum, item) => sum + packOf(item) * item.quantity, 0);
    // The stepper's top is per row; the guard as a whole stops at 10 of the
    // last pack too (or where it already is, if more).
    const target = Math.min(packs + delta, Math.max(variants.length * 10, packs));
    if (target < 1) throw new Error("No packs left");
    if (target === packs) return self.key;
    const properties = cleanProperties(self.properties);

    // Autoship wins: any line on a plan puts every new line on it.
    const withPlan = lines.find(planOf);
    const add = layout(target, variants.length).map(({ to, quantity }) => {
      const variant = variants[to - 1];
      // Sold out: say so before anything has been taken out, not after.
      if (!variant || variant.available === false) throw new Error("Pack unavailable");
      const entry = { id: variant.id, quantity, properties };
      if (withPlan) {
        const plan = mapPlan(variants, packOf(withPlan), to, planOf(withPlan), withPlan.selling_plan_allocation.selling_plan.name);
        // An Autoship line with no Autoship on the target: refuse, never
        // quietly turn it into a one-time purchase.
        if (!plan) throw new Error("No matching selling plan");
        entry.selling_plan = Number(plan);
      }
      return entry;
    });
    if (shape(add) === shape(lines)) return self.key;

    const restore = lines.map((item) => {
      const entry = { id: item.variant_id, quantity: item.quantity, properties: cleanProperties(item.properties) };
      if (planOf(item)) entry.selling_plan = planOf(item);
      return entry;
    });

    const sectionsToRender = cartItems.getSectionsToRender();
    const sectionIds = sectionsToRender.map((section) => section.section);
    if (pageDrawer(cartItems)) sectionIds.push("cart-drawer");
    const withSections = { sections: sectionIds, sections_url: window.location.pathname };

    const updates = {};
    lines.forEach((item) => (updates[item.key] = 0));
    const removed = await post(`${routes.cart_update_url}.js`, { updates });
    if (!removed.ok) throw new Error("Remove failed");

    /* From here the old lines are out of the cart. Anything that goes wrong,
       a network error included, has to end in the restore below, never in a
       bare throw that leaves the cart short under rows still showing them. */
    const attempt = async (body, tracked) => {
      try {
        const response = await post(`${routes.cart_add_url}.js`, body, tracked);
        return response.ok ? await response.json() : null;
      } catch (e) {
        return null;
      }
    };
    const cart = await removed.json().catch(() => ({ item_count: 0 }));
    const count = (list) => list.reduce((sum, entry) => sum + entry.quantity, 0);

    /* More packs is a real add, so that one goes through the page's fetch
       and reaches analytics as an Add to Cart. Fewer packs, and putting
       lines back, are not adds the shopper made. See untrackedCartFetch. */
    const result = await attempt({ items: add, ...withSections }, delta > 0);
    if (!result) {
      /* Should part of the add have gone in before it failed, take it out
         first, so putting the old lines back cannot leave extra packs. */
      try {
        const now = await fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } }).then((r) => r.json());
        const partial = {};
        (now.items || [])
          .filter(
            (item) =>
              item.product_id === self.product_id &&
              !isGift(item) &&
              item.final_price > 0 &&
              packOf(item) &&
              props(item) === props(self)
          )
          .forEach((item) => (partial[item.key] = 0));
        if (Object.keys(partial).length) await post(`${routes.cart_update_url}.js`, { updates: partial });
      } catch (e) {
        // Could not read the cart: put the lines back regardless.
      }
      const restored = await attempt({ items: restore, ...withSections });
      if (!restored) {
        // The lines are gone and would not come back: show the cart as it is.
        await cartItems.renderFromSections();
        throw new Error("Swap and restore failed");
      }
      await paint(cartItems, sectionsToRender, restored.sections || {}, cart.item_count + count(restore));
      throw new Error("Swap failed, lines restored");
    }
    // add.js answers with the lines it added, not the cart: the count is the
    // cart without the old lines plus the new lines' quantities.
    await paint(cartItems, sectionsToRender, result.sections || {}, cart.item_count + count(add));
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: null, variantId: add[0].id });
    }
    // Focus goes back to the row on the tapped line's pack if there still is one, else the first.
    const added = (result.items || []);
    const same = added.find((item) => item.variant_id === self.variant_id) || added[0];
    return same ? same.key : null;
  };

  /* The re-render replaced the control the shopper was on. Put focus back on
     the same control in the line's new row, as a quantity change does — and,
     as there, never while the drawer is open (see cart.js updateQuantity). */
  const refocus = (scope, key, selector) => {
    if (!key || !selector) return;
    const drawer = document.querySelector("cart-drawer");
    if (drawer && drawer.classList.contains("active")) return;
    // Within this cart: on /cart the closed drawer holds a row with the same key.
    const live = (scope.isConnected ? scope : document).querySelector(`.cart-item[data-line-key="${CSS.escape(key)}"]`);
    const control = live && live.querySelector(selector);
    if (control) control.focus({ preventScroll: true });
  };

  const start = async (cartItems, row, input, delta) => {
    swapping = true;
    const line = input.dataset.index;
    /* Invalidate any quantity response still in flight, so it cannot paint
       the line as it was over the swap. */
    const root = cartItems.closest("cart-drawer") || cartItems;
    root._qtyToken = (root._qtyToken || 0) + 1;
    // Keeps the drawer's scrim up past its own timeout (cart-drawer.js lockQuantity).
    window.cartQuantityRequests = (window.cartQuantityRequests || 0) + 1;
    const active = document.activeElement;
    const focusSelector =
      active && row.contains(active)
        ? active.matches(".quantity__button")
          ? `.quantity__button[name="${active.getAttribute("name")}"]`
          : ".quantity__input"
        : null;
    cartItems.enableLoading(line);
    setControls(row, true);
    // The line to put focus back on: the new one, or the old one when the swap did not take.
    let focusKey = row.dataset.lineKey;
    try {
      const key = await window.AirlockCartLock.run(() => swap(cartItems, row, delta));
      // Announces the new cart and drops the "loading" status enableLoading raised.
      cartItems.updateLiveRegions(line, "");
      focusKey = key;
    } catch (error) {
      console.error(error);
      input.value = row.dataset.packCount || row.dataset.packPosition;
      if (cartItems.lineItemStatusElement) cartItems.lineItemStatusElement.setAttribute("aria-hidden", true);
      if (row.isConnected) showError(row);
      else {
        const live = document.querySelector(`.cart-item[data-line-key="${CSS.escape(row.dataset.lineKey)}"]`);
        if (live) showError(live);
      }
    } finally {
      window.cartQuantityRequests = Math.max(0, (window.cartQuantityRequests || 1) - 1);
      setControls(row, false);
      cartItems.disableLoading(line);
      swapping = false;
      refocus(cartItems, focusKey, focusSelector);
      // cart.js keeps the last full cart for its next quantity change; this
      // one changed under it.
      if (typeof rememberCart === "function") {
        fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })
          .then((response) => response.json())
          .then(rememberCart)
          .catch(() => {});
      }
    }
  };

  document.addEventListener(
    "change",
    (event) => {
      const input = event.target;
      if (!input.matches || !input.matches(".quantity__input")) return;
      const row = input.closest(".cart-item[data-pack-variants]");
      if (!row) return;
      const cartItems = row.closest("cart-drawer-items") || row.closest("cart-items");
      if (!cartItems) return;

      const from = parseInt(row.dataset.packPosition, 10);
      const quantity = parseInt(row.dataset.packQuantity, 10) || 1;
      // What the stepper shows: every pack on the line.
      const current = parseInt(row.dataset.packCount, 10) || from * quantity;
      const reset = () => {
        input.value = current;
      };
      // One swap at a time; anything else on a pack line waits for it.
      if (swapping) {
        event.stopImmediatePropagation();
        reset();
        return;
      }

      // A drawer +/- leaves the input alone and sends the number in detail.
      const raw = event.detail && event.detail.quantity !== undefined ? event.detail.quantity : input.value;
      const requested = parseInt(raw, 10);
      // 0 removes the line, the theme's way.
      if (requested === 0) return;

      event.stopImmediatePropagation();
      if (Number.isNaN(requested) || !readJSON(row.dataset.packVariants, []).length) return reset();
      // The stepper's own top (cart-pack-variant.liquid), for a typed number.
      const top = parseInt(input.max, 10);
      const wanted = Math.max(1, top ? Math.min(requested, top) : requested);
      // The same number again changes nothing.
      if (wanted === current) return reset();
      // The guard moves by what this row moved: 8 stepped down is 7 packs.
      start(cartItems, row, input, wanted - current);
    },
    true
  );
})();
